import { useContext, useEffect, useMemo } from 'react';
import { ApiContext } from '../contexts/ApiContext';

/**
 * Traduce el slug de producto de la URL al producto completo.
 *
 * La URL es la fuente de verdad: este hook la lee y dispara el fetch, nadie más.
 * El slug se resuelve a id contra el índice de /api/product-search (que ya se
 * carga al arrancar la app y trae id, slug y brand_slug de todo el catálogo),
 * así que no hace falta un endpoint por slug en el backend.
 */
export function useProductFromUrl(productSlug) {
    const {
        productSearch,
        productById,
        productByIdLoading,
        productByIdError,
        fetchProductById,
        emptyProductById,
    } = useContext(ApiContext);

    // slug -> item del índice. Se arma una sola vez por catálogo, no por render.
    const productIndex = useMemo(() => {
        if (!Array.isArray(productSearch)) return null;
        return new Map(productSearch.map(item => [item.slug, item]));
    }, [productSearch]);

    const indexEntry = productSlug && productIndex ? productIndex.get(productSlug) : undefined;
    const productId = indexEntry ? indexEntry.id : null;

    useEffect(() => {
        if (!productSlug) {
            emptyProductById();
            return;
        }
        if (!productIndex) return; // el índice todavía no llegó

        if (productId) {
            fetchProductById(productId);
        } else {
            emptyProductById(); // slug que no existe en el catálogo
        }
    }, [productSlug, productId, productIndex, fetchProductById, emptyProductById]);

    // Mientras el índice no esté, un slug en la URL todavía no se puede resolver:
    // eso también es "cargando", no "no encontrado".
    const resolvingIndex = Boolean(productSlug) && !productIndex;
    const notFound = Boolean(productSlug) && Boolean(productIndex) && !indexEntry;

    return {
        product: productSlug ? productById : null,
        indexEntry,
        loading: resolvingIndex || productByIdLoading,
        error: productByIdError,
        notFound,
    };
}
